import { isEqual, merge } from "lodash";
import {
  EXPORT_TYPE,
  FILE_TYPES,
} from "src/components/ExportTransactionsModal/ExportTransactionsModal";
import { APP_USER_ID_KEY } from "src/constants/app";
import {
  FREQUENT_TRANSACTIONS_TABS,
  NOTIFICATIONS_TABS,
  REPORTS_TABS,
  TRANSACTIONS_TABS,
} from "src/constants/tabs";
import { EDataView, EToggleView } from "src/helpers/constants";
import { create } from "zustand";

export enum EPaginationStyle {
  TOP = "TOP",
  BOTTOM = "BOTTOM",
  DOUBLE = "DOUBLE",
}

const DEFAULT_UI_PREFERENCES = {
  notes: {
    activeTab: 0,
  },
  notifications: {
    activeTab: NOTIFICATIONS_TABS.RECURRING_TRANSACTIONS,
  },
  attachments: {
    autoPreview: true,
  },
  transaction: {
    showDiscountField: false,
    showTaxesField: false,
    showVoucherField: false,
    showNotes: false,
    showDetails: false,
    showDocumentNumberField: false,
  },
  transactions: {
    activeTab: TRANSACTIONS_TABS.ACTIVE_TRANSACTIONS,
    viewAs: EToggleView.DATA,
    dataViewAs: EDataView.TABLE,
  },
  recurringTransactions: {
    activeTab: FREQUENT_TRANSACTIONS_TABS.ACTIVE_TRANSACTIONS,
    viewAs: EToggleView.DATA,
    dataViewAs: EDataView.TABLE,
  },
  reports: {
    activeTab: REPORTS_TABS.TRANSACTIONS_LAST_SEVEN_DAYS,
    viewAs: EToggleView.DATA,
    dataViewAs: EDataView.TABLE,
  },
  accounts: {
    showInactiveAccounts: true,
    allowTransfersWithNoFunds: true,
  },
  recentTransactions: {
    viewAs: EToggleView.DATA,
    dataViewAs: EDataView.TABLE,
  },
  tables: {
    allowMultipleSelection: true,
    paginationStyle: EPaginationStyle.BOTTOM,
    allowPinnedColumns: true,
    showColumnFilters: true,
    selectionOnClickAnyCell: true,
    showRowBorders: true,
    showColumnBorders: false,
    showTableBorder: false,
    showIncomeIcon: true,
    showExpenseIcon: true,
    showSourceIcon: true,
    showCategoryIcon: false,
    showStatusIcon: true,
    showDifferenceIcon: true,
    showDateIcon: true,
    showTimeIcon: true,
    showMonthIcon: true,
    showCountIcon: true,
    showBankIcon: true,
    showAccountIcon: true,
  },
  charts: {
    type: "bar",
    showLegend: true,
    showTooltips: true,
    showToolbox: true,
    showAnimations: true,
    showZoom: true,
    showPositiveExpenses: false,
    showReferenceLine: false,
    incomeSymbolOpacity: 0.9,
    expensesSymbolOpacity: 0.9,
    incomeLineOpacity: 1,
    expensesLineOpacity: 1,
    incomeSymbol: "circle",
    expensesSymbol: "circle",
    incomeSymbolSize: 20,
    expensesSymbolSize: 20,
  },
  misc: {
    enableMobileTitleBar: true,
    showDesktopRightPanel: true,
    showDesktopNavBar: true,
    enableComplexAnimations: true,
  },
  pinnedColumns: {
    transactions: {
      date: false,
      time: false,
      clientProvider: false,
      total: false,
      description: false,
      category: false,
      payment: false,
      status: false,
      actions: false,
    },
    reports: {
      last7Days: {
        day: false,
        count: false,
        income: false,
        expenses: false,
        difference: false,
      },
      months: {
        month: false,
        income: false,
        expenses: false,
        difference: false,
      },
      categories: {
        category: false,
        count: false,
        income: false,
        expenses: false,
        difference: false,
      },
      clientProviders: {
        name: false,
        count: false,
        income: false,
        expenses: false,
        difference: false,
      },
    },
    files: {
      date: false,
      typr: false,
      size: false,
      name: false,
      actions: false,
    },
    accounts: {
      name: false,
      number: false,
      currency: false,
      bank: false,
      difference: false,
      count: false,
      actions: false,
    },
    recurring: {
      status: false,
      date: false,
      clientProvider: false,
      description: false,
      category: false,
      total: false,
      actions: false,
    },
  },
  collapsibles: {},
  selectionTotals: {
    sum: true,
    income: true,
    expenses: true,
  },
  export: {
    type: EXPORT_TYPE.ALL,
    format: FILE_TYPES.XLSX,
    formatNumbers: true,
    includeCurrencySymbol: true,
    fileFormatOptions: {
      [FILE_TYPES.CSV]: {
        columnDelimiter: ",",
        rowDelimiter: "\\n",
      },
    },
  },
};

const cleanUiPreferences = (state: any) => ({
  notes: state.notes,
  notifications: state.notifications,
  attachments: state.attachments,
  transaction: state.transaction,
  transactions: state.transactions,
  recurringTransactions: state.recurringTransactions,
  reports: state.reports,
  accounts: state.accounts,
  recentTransactions: state.recentTransactions,
  tables: state.tables,
  charts: state.charts,
  misc: state.misc,
  pinnedColumns: state.pinnedColumns,
  collapsibles: state.collapsibles,
  selectionTotals: state.selectionTotals,
  export: state.export,
});

const getUiPreferencesStorageKey = (userId: string) => {
  return `${userId}-ui-preferences`;
};

const getStoredUiPreferences = () => {
  if (typeof window === "undefined") {
    return DEFAULT_UI_PREFERENCES;
  }

  try {
    const userId = localStorage.getItem(APP_USER_ID_KEY);

    if (!userId) {
      return DEFAULT_UI_PREFERENCES;
    }

    const raw = localStorage.getItem(getUiPreferencesStorageKey(userId));

    if (!raw) {
      return DEFAULT_UI_PREFERENCES;
    }

    const parsed = JSON.parse(raw);

    return merge({}, DEFAULT_UI_PREFERENCES, parsed);
  } catch (error) {
    console.error("Error loading ui preferences from localStorage", error);
    return DEFAULT_UI_PREFERENCES;
  }
};

const saveUiPreferences = (state: any) => {
  if (typeof window === "undefined") return;

  try {
    const userId = localStorage.getItem(APP_USER_ID_KEY);

    if (!userId) return;

    localStorage.setItem(
      getUiPreferencesStorageKey(userId),
      JSON.stringify(cleanUiPreferences(state)),
    );
  } catch (error) {
    console.error("Error saving ui preferences to localStorage", error);
  }
};

const removeUiPreferences = () => {
  if (typeof window === "undefined") return;

  try {
    const userId = localStorage.getItem(APP_USER_ID_KEY);

    if (!userId) return;

    localStorage.removeItem(getUiPreferencesStorageKey(userId));
  } catch (error) {
    console.error("Error removing ui preferences from localStorage", error);
  }
};

const useUiPreferences = create((set: any, get: any) => ({
  ...getStoredUiPreferences(),

  loadUiPreferences: () => {
    const storedPreferences = getStoredUiPreferences();
    set(storedPreferences);
  },

  resetUiPreferences: () => {
    removeUiPreferences();
    set({ ...DEFAULT_UI_PREFERENCES });
  },

  setUiPreferences: (updates: any) => {
    const state = get();
    const currentData = cleanUiPreferences(state);
    const newState = merge({}, currentData, updates);

    if (!isEqual(currentData, newState)) {
      set(newState);
      saveUiPreferences(newState);
    }
  },
}));

export default useUiPreferences;
